'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { Search } from 'lucide-react'
import { useDocumentationContext } from './documentation-provider'
import { parseTrainingGuide } from '@/utils/documentation-parser'
import { useDocumentationContent } from '@/hooks/useDocumentationContent'
import { cn } from '@/lib/utils'

interface SearchResult {
  sectionId: string
  sectionTitle: string
  articleId: string
  articleTitle: string
  snippet: string
}

export function DocumentationSearch() {
  const { searchQuery, setSearchQuery, openArticle } = useDocumentationContext()
  const { content } = useDocumentationContent()
  const [localQuery, setLocalQuery] = useState(searchQuery)
  const [results, setResults] = useState<SearchResult[]>([])
  const [showResults, setShowResults] = useState(false)

  // Parse the training guide into sections
  const sections = useMemo(() => {
    if (!content) return []
    return parseTrainingGuide(content)
  }, [content])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [localQuery, setSearchQuery])

  // Search through content
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([])
      setShowResults(false)
      return
    }

    const query = searchQuery.toLowerCase()
    const matches: SearchResult[] = []

    sections.forEach(section => {
      section.articles.forEach(article => {
        // Search in title
        if (article.title.toLowerCase().includes(query)) {
          matches.push({
            sectionId: section.id,
            sectionTitle: section.title,
            articleId: article.id,
            articleTitle: article.title,
            snippet: getSnippet(article.content, query)
          })
        }
        // Search in content
        else if (article.content.toLowerCase().includes(query)) {
          matches.push({
            sectionId: section.id,
            sectionTitle: section.title,
            articleId: article.id,
            articleTitle: article.title,
            snippet: getSnippet(article.content, query)
          })
        }
      })
    })

    setResults(matches.slice(0, 10)) // Limit to 10 results
    setShowResults(matches.length > 0)
  }, [searchQuery, sections])

  // Extract snippet around search term
  const getSnippet = (content: string, query: string): string => {
    const index = content.toLowerCase().indexOf(query)
    if (index === -1) return content.slice(0, 100) + '...'
    
    const start = Math.max(0, index - 50)
    const end = Math.min(content.length, index + query.length + 50)
    
    let snippet = content.slice(start, end)
    if (start > 0) snippet = '...' + snippet
    if (end < content.length) snippet = snippet + '...'
    
    return snippet
  }

  const handleResultClick = (result: SearchResult) => {
    openArticle(result.sectionId, result.articleId)
    setLocalQuery('')
    setShowResults(false)
  }

  return (
    <div className="relative border-b border-gray-200">
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            onFocus={() => setShowResults(results.length > 0)}
            placeholder="Search documentation..."
            className={cn(
              "w-full pl-10 pr-4 py-2",
              "border border-gray-300 rounded-lg",
              "text-sm text-gray-900",
              "placeholder:text-gray-500",
              "focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            )}
          />
        </div>
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 z-10 bg-white border-b border-x border-gray-200 shadow-lg max-h-96 overflow-y-auto">
          {results.map((result, index) => (
            <button
              key={`${result.sectionId}-${result.articleId}-${index}`}
              onClick={() => handleResultClick(result)}
              className={cn(
                "w-full text-left px-4 py-3",
                "hover:bg-purple-50",
                "border-b border-gray-100 last:border-b-0",
                "focus:outline-none focus:bg-purple-50"
              )}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {result.articleTitle}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {result.sectionTitle}
                  </div>
                  <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                    {result.snippet}
                  </div>
                </div>
              </div>
            </button>
          ))}
          
          {results.length === 0 && searchQuery.length >= 2 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              No results found for "{searchQuery}"
            </div>
          )}
        </div>
      )}
    </div>
  )
}